import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ToggleLeft, ToggleRight, Info } from "@phosphor-icons/react";
import api from "../../lib/api";
import { FEATURE_LABELS } from "../../lib/featureAccess";

export default function FeatureTogglesPanel() {
  const [toggleable, setToggleable] = useState([]);
  const [disabled, setDisabled] = useState(new Set());
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/feature-toggles");
      setToggleable(data.toggleable || []);
      setDisabled(new Set(data.disabled || []));
      setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
    } catch (e) {
      toast.error(
        "Failed to load feature toggles: " +
          (e.response?.data?.detail || e.message)
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const toggle = async (slug, currentlyDisabled) => {
    setSaving(slug);
    try {
      const { data } = await api.put("/admin/feature-toggles", {
        slug,
        enabled: currentlyDisabled,
      });
      setDisabled(new Set(data.disabled || []));
      toast.success(
        `${FEATURE_LABELS[slug] || slug} ${
          currentlyDisabled ? "enabled" : "disabled"
        } for non-admin users`
      );
    } catch (e) {
      toast.error(
        "Toggle failed: " + (e.response?.data?.detail || e.message)
      );
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="text-muted-foreground" data-testid="feature-toggles-loading">
        Loading feature toggles…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="feature-toggles-panel">
      <header>
        <h2 className="font-serif text-2xl mb-1">Feature toggles</h2>
        <p className="text-sm text-muted-foreground max-w-2xl leading-relaxed">
          Globally enable or disable individual features for end-users.
          Disabling a feature hides it from the sidebar and blocks access
          across all Basic/Pro/Free users. <strong>Admins are always
          exempt</strong> — you keep full access even when a feature is
          turned off.
        </p>
      </header>

      <div
        className="rounded-md border hairline p-3 text-xs text-muted-foreground flex items-start gap-2 bg-[hsl(var(--muted))]/40"
        data-testid="feature-toggles-warning"
      >
        <Info size={14} weight="duotone" className="mt-0.5 shrink-0" />
        <span>
          Disabling a feature that customers paid for through an add-on may
          trigger support tickets. Prefer using this for beta features,
          incident containment, or gradual rollout — not permanent removal.
        </span>
      </div>

      <div className="rounded-md border hairline overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-xs">
            <tr className="border-b hairline bg-[hsl(var(--muted))]/30">
              <th className="text-left py-3 px-4">Feature</th>
              <th className="text-left py-3 px-4">Slug</th>
              <th className="text-left py-3 px-4">Status for users</th>
              <th className="text-right py-3 px-4">Action</th>
            </tr>
          </thead>
          <tbody>
            {toggleable.map((slug) => {
              const isDisabled = disabled.has(slug);
              const label = FEATURE_LABELS[slug] || slug;
              return (
                <tr
                  key={slug}
                  className="border-b hairline last:border-0"
                  data-testid={`feature-toggle-row-${slug}`}
                >
                  <td className="py-3 px-4 font-serif">{label}</td>
                  <td className="py-3 px-4 font-mono text-xs text-muted-foreground">
                    {slug}
                  </td>
                  <td className="py-3 px-4">
                    {isDisabled ? (
                      <span
                        className="inline-block px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-600 border border-amber-500/30"
                        data-testid={`feature-toggle-status-${slug}`}
                      >
                        Disabled for users
                      </span>
                    ) : (
                      <span
                        className="inline-block px-2 py-0.5 text-xs rounded-full bg-emerald-500/20 text-emerald-600 border border-emerald-500/30"
                        data-testid={`feature-toggle-status-${slug}`}
                      >
                        Enabled
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      type="button"
                      onClick={() => toggle(slug, isDisabled)}
                      disabled={saving === slug}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border hairline rounded-md hover:bg-[hsl(var(--muted))]/60"
                      data-testid={`feature-toggle-btn-${slug}`}
                    >
                      {isDisabled ? (
                        <>
                          <ToggleLeft size={16} weight="duotone" />
                          Enable
                        </>
                      ) : (
                        <>
                          <ToggleRight
                            size={16}
                            weight="duotone"
                            className="text-[hsl(var(--primary))]"
                          />
                          Disable
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {meta.updated_at && (
        <div className="text-xs text-muted-foreground" data-testid="feature-toggles-meta">
          Last updated {new Date(meta.updated_at).toLocaleString()}
          {meta.updated_by ? ` by ${meta.updated_by}` : ""}
        </div>
      )}
    </div>
  );
}
